// Public UI adapted from Klargo-Reference/pages/LegalDocument.tsx.
import { Helmet } from '../adapters/Helmet.jsx';
import Header from '../components/layout/Header.jsx';
import Footer from '../components/layout/Footer.jsx';
import { useLanguage } from '../components/layout/LanguageSwitcher.jsx';
import { getPrivacyPolicyContent } from '../content/legal/privacyPolicy.jsx';
import { getTermsContent } from '../content/legal/termsOfUse.jsx';
import { COOKIES_BLOCKS_AFTER_TABLE, COOKIES_BLOCKS_BEFORE_TABLE, COOKIES_INTRO, COOKIES_TITLE, COOKIES_UPDATED, COOKIE_TABLE_COLUMNS, COOKIE_TABLE_ROWS, } from '../content/legal/cookies.jsx';
const PATHS = {
    privacy: 'integritet',
    terms: 'villkor',
    cookies: 'cookies',
};
const MAIL_PATTERN = /(info@klargo\.se|support@klargo\.se)/g;
const withMailLinks = (text) => text.split(MAIL_PATTERN).map((part, i) => MAIL_PATTERN.test(part) ? (<a key={i} href={`mailto:${part}`} className="text-ds-ink underline underline-offset-2">
        {part}
      </a>) : (part));
const bodyClass = 'm-0 text-[15.5px] leading-[1.65] text-ds-text';
const LegalBlockView = ({ block }) => {
    switch (block.kind) {
        case 'section':
            return (<h2 className="m-0 mt-11 border-t border-ds-rule-row pt-6 font-ds-serif text-[21px] font-bold leading-[1.25] tracking-[-0.01em] text-ds-ink first:mt-0">
          {block.text}
        </h2>);
        case 'subheading':
            return (<h3 className="m-0 mt-7 text-[15.5px] font-semibold leading-[1.4] text-ds-ink">
          {block.text}
        </h3>);
        case 'paragraph':
            return <p className={`${bodyClass} mt-3`}>{withMailLinks(block.text)}</p>;
        case 'definition':
            return (<p className={`${bodyClass} mt-3`}>
          <strong className="font-semibold text-ds-ink">{block.term}:</strong>{' '}
          {withMailLinks(block.text)}
        </p>);
        case 'list':
            return (<ul className="m-0 mt-3 list-disc space-y-1.5 pl-[1.15em] marker:text-ds-ink/60">
          {block.items.map((item) => (<li key={item} className={`${bodyClass} pl-1`}>
              {item}
            </li>))}
        </ul>);
        default:
            return null;
    }
};
const CookieRegister = () => (<div className="mt-4 -mx-6 overflow-x-auto px-6 md:mx-0 md:px-0">
    <table className="w-full min-w-[720px] border-collapse text-[13px] leading-[1.5]">
      <thead>
        <tr>
          {COOKIE_TABLE_COLUMNS.map((column) => (<th key={column} scope="col" className="border-b border-ds-ink px-2 py-2 text-left align-bottom font-semibold text-ds-ink">
              {column}
            </th>))}
        </tr>
      </thead>
      <tbody>
        {COOKIE_TABLE_ROWS.map((row, rowIndex) => (<tr key={rowIndex}>
            {row.map((cell, cellIndex) => (<td key={cellIndex} className="border-b border-ds-rule-row px-2 py-2 align-top text-ds-text">
                {cell}
              </td>))}
          </tr>))}
      </tbody>
    </table>
  </div>);
const LegalDocument = ({ kind }) => {
    const { t, languageCode } = useLanguage();
    const canonical = `https://klargo.lovable.app/${PATHS[kind]}`;
    const doc = kind === 'privacy'
        ? (() => {
            const c = getPrivacyPolicyContent(languageCode);
            return { title: c.title, dates: [c.effective, c.updated], intro: c.intro, blocks: c.blocks };
        })()
        : kind === 'terms'
            ? (() => {
                const c = getTermsContent(languageCode);
                return { title: c.title, dates: [c.effective, c.updated], intro: c.intro, blocks: c.blocks };
            })()
            : {
                title: COOKIES_TITLE,
                dates: [COOKIES_UPDATED],
                intro: COOKIES_INTRO,
                blocks: COOKIES_BLOCKS_BEFORE_TABLE,
                tail: COOKIES_BLOCKS_AFTER_TABLE,
            };
    return (<div className="ds-scope flex min-h-screen min-w-[320px] flex-col bg-ds-canvas font-ds-sans">
      <Helmet>
        
        <html lang={languageCode}/>
        <title>{t(`legal.${kind}.docTitle`)}</title>
        <meta name="description" content={t(`legal.${kind}.docDescription`)}/>
        <link rel="canonical" href={canonical}/>
        <meta property="og:title" content={t(`legal.${kind}.docTitle`)}/>
        <meta property="og:description" content={t(`legal.${kind}.docDescription`)}/>
        <meta property="og:url" content={canonical}/>
        <meta property="og:type" content="article"/>
        <meta name="twitter:card" content="summary"/>
      </Helmet>

      <Header />

      <main id="main-content" className="flex-1">
        
        <article className="mx-auto max-w-[720px] px-6 py-14 md:px-8 md:py-20" lang={languageCode}>
          <h1 className="m-0 font-ds-serif text-[clamp(28px,3.4vw,38px)] font-bold leading-[1.1] tracking-[-0.015em] text-ds-ink">
            {doc.title}
          </h1>

          
          <p className="m-0 mt-4 text-[13.5px] leading-[1.55] text-ds-muted">
            {doc.dates.map((line) => (<span key={line} className="block">
                {line}
              </span>))}
          </p>

          <div className="mt-7 border-t-2 border-ds-ink pt-6">
            {doc.intro.map((block, i) => (<LegalBlockView key={i} block={block}/>))}
          </div>

          <div className="mt-10">
            {doc.blocks.map((block, i) => (<LegalBlockView key={i} block={block}/>))}
            {doc.tail ? (<>
                <CookieRegister />
                {doc.tail.map((block, i) => (<LegalBlockView key={i} block={block}/>))}
              </>) : null}
          </div>
        </article>
      </main>


      <Footer ground="canvas"/>
    </div>);
};
export default LegalDocument;
